import { useMemo } from "react"

import SearchSelect, {
  type Option as SearchOption,
} from "~/ui-rtk/components/ui/common/SearchSelect"
import { MultiSelect, Select } from "~/ui-rtk/components/ui/controls"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"

import type { SubsequentFilters } from "../connect"
import {
  GRAIN_OPTIONS,
  ORDER_SEQUENCE_OPTIONS,
  SKU_PREFIX_LEVELS,
  type SubsequentGrain,
} from "../constants"

export type SubsequentFiltersProps = {
  filters: SubsequentFilters
  onChange: (filters: SubsequentFilters) => void
  itemOptions: { options: SearchOption[]; isLoading: boolean }
}

const toSelected = (
  values: string[],
  options: TMultiSelectOption[],
): TMultiSelectOption[] =>
  options.filter(option => values.includes(option.value))

export default function SubsequentFiltersBar({
  filters,
  onChange,
  itemOptions,
}: SubsequentFiltersProps) {
  const { isEnabled: isDemoEnabled } = useDemoMode()

  const selectedKey =
    filters.grain === "product" ? filters.productId : filters.skuPrefix

  // The selection may fall outside the top-N option list for the window (or
  // the list may be mid-refetch); appending it keeps the pick visible.
  const optionsWithSelected = useMemo<SearchOption[]>(() => {
    if (!selectedKey) return itemOptions.options
    const known = itemOptions.options.some(
      option => String(option.value) === selectedKey,
    )
    return known
      ? itemOptions.options
      : [...itemOptions.options, { value: selectedKey, label: selectedKey }]
  }, [itemOptions.options, selectedKey])

  const selectedOption = useMemo(
    () =>
      selectedKey
        ? optionsWithSelected.find(
            option => String(option.value) === selectedKey,
          )
        : undefined,
    [optionsWithSelected, selectedKey],
  )

  const itemLabel =
    filters.grain === "product"
      ? "Product bought"
      : `SKU prefix bought (first ${filters.skuPrefixLength} char${
          filters.skuPrefixLength === 1 ? "" : "s"
        })`

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-light-2">Analysis type</span>
        <div className="min-w-40">
          <Select
            value={filters.grain}
            options={GRAIN_OPTIONS}
            onChange={event =>
              onChange({
                ...filters,
                grain: event.target.value as SubsequentGrain,
                // Selections are grain-specific; a grain switch resets them.
                productId: null,
                skuPrefix: null,
              })
            }
          />
        </div>
      </div>
      {filters.grain === "sku" && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-light-2">Prefix length</span>
          <div className="w-36">
            <Select
              value={String(filters.skuPrefixLength)}
              options={SKU_PREFIX_LEVELS.map(level => ({
                value: String(level),
                label: `First ${level} char${level === 1 ? "" : "s"}`,
              }))}
              onChange={event =>
                onChange({
                  ...filters,
                  skuPrefixLength: Number(event.target.value),
                  // Prefixes are level-specific; a level switch resets them.
                  skuPrefix: null,
                })
              }
            />
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1 min-w-80">
        <span className="text-xs text-gray-light-2">
          {itemLabel}
          {itemOptions.isLoading ? " (loading…)" : ""}
        </span>
        <SearchSelect
          key={`${filters.grain}-${filters.skuPrefixLength}`}
          options={optionsWithSelected}
          value={selectedOption}
          demoMode={isDemoEnabled && filters.grain === "product"}
          onSelect={option => {
            const value = String(option.value)
            onChange(
              filters.grain === "product"
                ? { ...filters, productId: value }
                : { ...filters, skuPrefix: value },
            )
          }}
        />
      </div>
      <MultiSelect
        label="Original order"
        options={ORDER_SEQUENCE_OPTIONS}
        value={toSelected(filters.orderSequences, ORDER_SEQUENCE_OPTIONS)}
        onChange={selected =>
          onChange({ ...filters, orderSequences: selected.map(o => o.value) })
        }
        className="min-w-44"
      />
    </div>
  )
}
